import { useCallback, useEffect, useRef, useState } from 'react';

/** No bytes for this long and the transfer is treated as stalled. */
export const STALL_THRESHOLD_MS = 25_000;

/** How often the watchdog looks. */
export const WATCHDOG_INTERVAL_MS = 5_000;

export interface ModelLoadWatchdog {
  /** True once the transfer has gone quiet for [STALL_THRESHOLD_MS]. */
  stalled: boolean;
  /** Call on every progress event. Clears a stall and restarts the clock. */
  markProgress: () => void;
}

/**
 * Watches an in-flight model download and reports when it goes quiet.
 *
 * The web has no equivalent of the Flutter client's silent reconnect: the
 * fetching is inside transformers.js, so there is no socket here to tear down
 * and re-establish. What this can do is *notice* — which is the part that was
 * missing, because a stalled download and a slow one look identical on a
 * progress bar that has simply stopped moving.
 *
 * Deliberately **wall-clock** rather than a countdown: browsers throttle timers
 * in background tabs to once a minute or less, so a decrementing counter would
 * lose time whenever the user switched away — exactly when a stall is most
 * likely to go unnoticed. Comparing timestamps means a tab that was throttled
 * for ten minutes trips on its first tick back.
 *
 * @param active whether a load is currently running
 * @param onStalled fired once per stall, on the transition into it
 */
export function useModelLoadWatchdog(
  active: boolean,
  onStalled?: () => void,
): ModelLoadWatchdog {
  const [stalled, setStalled] = useState(false);
  const lastProgressRef = useRef<number>(Date.now());
  const stalledRef = useRef(false);

  // Ref so the interval below never has to be torn down and rebuilt just
  // because the caller passed a new closure.
  const onStalledRef = useRef(onStalled);
  useEffect(() => {
    onStalledRef.current = onStalled;
  }, [onStalled]);

  const markProgress = useCallback(() => {
    lastProgressRef.current = Date.now();
    if (!stalledRef.current) return;
    stalledRef.current = false;
    setStalled(false);
  }, []);

  useEffect(() => {
    if (!active) {
      stalledRef.current = false;
      setStalled(false);
      return;
    }
    // A load that starts gets a full threshold before it can be called stalled,
    // rather than inheriting a timestamp from a previous attempt.
    lastProgressRef.current = Date.now();

    const id = window.setInterval(() => {
      if (Date.now() - lastProgressRef.current < STALL_THRESHOLD_MS) return;
      if (stalledRef.current) return;
      stalledRef.current = true;
      setStalled(true);
      onStalledRef.current?.();
    }, WATCHDOG_INTERVAL_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [active]);

  return { stalled, markProgress };
}
